// Toasts and the "busy" modal used while an export is being saved.
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { useI18n } from '../i18n/I18nProvider';

type ToastKind = 'ok' | 'err' | '';
interface Toast {
  id: number;
  msg: string;
  kind: ToastKind;
  out: boolean;
}
interface BusyState {
  title: string;
  msg: string;
  done: boolean;
  err: boolean;
}
export interface BusyHandle {
  say(msg: string): void;
  /** shows the final message with a Close button; resolves when closed */
  done(msg: string, isErr?: boolean): Promise<void>;
  close(): void;
}
interface UiApi {
  toast(msg: string, kind?: ToastKind): void;
  busy(title: string): BusyHandle;
}

const Ctx = createContext<UiApi | null>(null);

export function UiProvider({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [busy, setBusy] = useState<BusyState | null>(null);
  const closeResolve = useRef<(() => void) | null>(null);
  const nextId = useRef(1);

  const toast = useCallback((msg: string, kind: ToastKind = '') => {
    const id = nextId.current++;
    setToasts((l) => [...l, { id, msg, kind, out: false }]);
    setTimeout(() => {
      setToasts((l) => l.map((x) => (x.id === id ? { ...x, out: true } : x)));
      setTimeout(() => setToasts((l) => l.filter((x) => x.id !== id)), 400);
    }, kind === 'err' ? 7000 : 3500);
  }, []);

  const busyFn = useCallback((title: string): BusyHandle => {
    setBusy({ title, msg: '', done: false, err: false });
    return {
      say: (msg) => setBusy((b) => (b ? { ...b, msg } : b)),
      done: (msg, isErr) =>
        new Promise<void>((resolve) => {
          closeResolve.current = resolve;
          setBusy((b) => (b ? { ...b, msg, done: true, err: !!isErr } : b));
        }),
      close: () => setBusy(null),
    };
  }, []);

  const api = useMemo(() => ({ toast, busy: busyFn }), [toast, busyFn]);
  return (
    <Ctx.Provider value={api}>
      {children}
      {busy && (
        <div className="uco-modal-back" role="dialog" aria-modal="true">
          <div className="uco-modal">
            <div className="uco-modal-title">{busy.title}</div>
            <div className="uco-modal-body" role="status">
              {!busy.done && <span className="uco-spin" />}
              <span className={'uco-busy-msg' + (busy.err ? ' err' : '')}>{busy.msg}</span>
            </div>
            {busy.done && (
              <div className="uco-modal-acts">
                <button
                  className="primary"
                  style={{ background: 'var(--amber)', color: 'var(--ink)', border: '1px solid var(--amber)', padding: '8px 13px', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
                  onClick={() => {
                    setBusy(null);
                    closeResolve.current?.();
                  }}
                >
                  {t('close')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {toasts.length > 0 && (
        <div className="uco-toasts" aria-live="polite">
          {toasts.map((x) => (
            <div key={x.id} className={'uco-toast ' + x.kind + (x.out ? ' out' : '')}>
              {x.msg}
            </div>
          ))}
        </div>
      )}
    </Ctx.Provider>
  );
}

export function useUi(): UiApi {
  const v = useContext(Ctx);
  if (!v) throw new Error('useUi outside UiProvider');
  return v;
}
